import React, { useState, useEffect } from 'react';
import { Product } from '../types';
import { showToast } from '../utils';

interface ReportsViewProps {
  active: boolean;
  products: Product[];
}

export interface EmailReportConfig {
  adminEmail: string;
  ccEmail: string;
  dailySummaryEnabled: boolean;
  dailySummaryTime: string;
  timezone: string;
  instantSaleAlertEnabled: boolean;
  includeAdsTelemetry: boolean;
  includeCatalogFixSummary: boolean;
  includeMonthlyComparison: boolean;
  transportMode: 'auto' | 'smtp' | 'resend';
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  resendApiKey: string;
  lastDailySentDate?: string | null;
  lastOrderPollAt?: string | null;
  notifiedOrderIds?: string[];
  updatedAt?: string;
}

export interface EmailReportLogEntry {
  id: string;
  type: 'daily_9pm_summary' | 'instant_sale_alert';
  trigger: string;
  to: string;
  cc?: string;
  subject: string;
  deliveryMethod: string;
  deliveredExternally: boolean;
  errorDetail?: string | null;
  sentAt: string;
  html: string;
}

const DEFAULT_REPORT_CONFIG: EmailReportConfig = {
  adminEmail: 'pprevalon@icloud.com',
  ccEmail: 'pprevalon@gmail.com',
  dailySummaryEnabled: true,
  dailySummaryTime: '21:00',
  timezone: 'America/New_York',
  instantSaleAlertEnabled: true,
  includeAdsTelemetry: true,
  includeCatalogFixSummary: true,
  includeMonthlyComparison: true,
  transportMode: 'auto',
  smtpHost: 'smtp.mail.me.com',
  smtpPort: 587,
  smtpUser: 'pprevalon@icloud.com',
  smtpPass: '',
  resendApiKey: ''
};

export const ReportsView: React.FC<ReportsViewProps> = ({ active, products }) => {
  const [config, setConfig] = useState<EmailReportConfig>(() => {
    try {
      const saved = localStorage.getItem('aurelius_email_report_config_v1');
      if (saved) return { ...DEFAULT_REPORT_CONFIG, ...JSON.parse(saved) };
    } catch (_) {}
    return DEFAULT_REPORT_CONFIG;
  });

  const [logs, setLogs] = useState<EmailReportLogEntry[]>([]);
  const [previewTab, setPreviewTab] = useState<'daily' | 'sale' | 'custom'>('daily');
  const [dailyPreviewHtml, setDailyPreviewHtml] = useState<string>('');
  const [dailyPreviewSubject, setDailyPreviewSubject] = useState<string>(
    '[AURELIUS 9PM REPORT] Today Summary · Orders, Revenue, Ad Clicks & Catalog Status'
  );
  const [salePreviewHtml, setSalePreviewHtml] = useState<string>('');
  const [salePreviewSubject, setSalePreviewSubject] = useState<string>(
    '💰 [NEW AMAZON SALE] +$35.55 (Net Profit: +$19.41) · Instant Order Alert'
  );
  const [selectedLogEntry, setSelectedLogEntry] = useState<EmailReportLogEntry | null>(null);
  const [savingConfig, setSavingConfig] = useState<boolean>(false);
  const [sendingDaily, setSendingDaily] = useState<boolean>(false);
  const [sendingSale, setSendingSale] = useState<boolean>(false);
  const [showSmtpSettings, setShowSmtpSettings] = useState<boolean>(false);

  // Pull today's logged metrics from Calendar localStorage if available
  const getTodayCalendarMetrics = () => {
    try {
      const calRaw = localStorage.getItem('aurelius_calendar_actuals_v1');
      if (calRaw) {
        const parsed = JSON.parse(calRaw);
        const oct = parsed['2026-10'] || {};
        const todayNum = new Date().getDate();
        const dayLog = oct.dailyLogs?.[todayNum] || {};
        return {
          todayOrders: Number(dayLog.orders || 0),
          todayRevenue: Number(dayLog.revenue || 0),
          todayClicks: Number(dayLog.clicks || 0),
          todayAdSpend: Number(dayLog.adSpend || 0),
          todayNetProfit: Number(dayLog.netProfit || 0),
          mtdOrders: Number(oct.actualOrders || 0),
          mtdRevenue: Number(oct.actualRevenue || 0)
        };
      }
    } catch (_) {}
    return {
      todayOrders: 0,
      todayRevenue: 0,
      todayClicks: 0,
      todayAdSpend: 0,
      todayNetProfit: 0,
      mtdOrders: 0,
      mtdRevenue: 0
    };
  };

  useEffect(() => {
    if (!active) return;
    fetch('/api/reports/status')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.ok) return;
        if (data.config) {
          setConfig((prev) => ({ ...prev, ...data.config }));
          try {
            localStorage.setItem('aurelius_email_report_config_v1', JSON.stringify(data.config));
          } catch (_) {}
        }
        if (Array.isArray(data.logs)) {
          setLogs(data.logs);
        }
        if (data.previews?.dailySummary) {
          setDailyPreviewHtml(data.previews.dailySummary.html || '');
          setDailyPreviewSubject(data.previews.dailySummary.subject || '');
        }
        if (data.previews?.saleAlert) {
          setSalePreviewHtml(data.previews.saleAlert.html || '');
          setSalePreviewSubject(data.previews.saleAlert.subject || '');
        }
      })
      .catch(() => {});
  }, [active]);

  const handleSaveConfig = async (nextCfg?: EmailReportConfig) => {
    const toSave = nextCfg || config;
    setSavingConfig(true);
    try {
      localStorage.setItem('aurelius_email_report_config_v1', JSON.stringify(toSave));
      const res = await fetch('/api/reports/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toSave)
      });
      const data = await res.json();
      if (data.ok && data.config) {
        setConfig(data.config);
      }
      showToast(`Saved Email Report & Alert settings for ${toSave.adminEmail}`);
    } catch (_) {
      showToast(`Saved Email Report settings locally for ${toSave.adminEmail}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSendDailySummaryNow = async () => {
    setSendingDaily(true);
    try {
      await handleSaveConfig(config);
      const metrics = getTodayCalendarMetrics();
      const res = await fetch('/api/reports/send-daily', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...metrics,
          trigger: 'manual_dispatch'
        })
      });
      const data = await res.json();
      if (data.ok && data.logEntry) {
        setLogs(data.logs || [data.logEntry, ...logs]);
        setDailyPreviewHtml(data.logEntry.html);
        setDailyPreviewSubject(data.logEntry.subject);
        setPreviewTab('daily');
        showToast(`Dispatched Today's Summary Report to ${config.adminEmail}`);
      }
    } catch (err) {
      showToast('Failed to dispatch summary report');
    } finally {
      setSendingDaily(false);
    }
  };

  const handleSendTestSaleAlert = async () => {
    setSendingSale(true);
    try {
      await handleSaveConfig(config);
      const heroProduct = products[0];
      const res = await fetch('/api/reports/send-sale-alert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trigger: 'manual_test',
          productTitle: heroProduct?.title || 'AURELIUS Electric Spin Scrubber Cordless Cleaning Brush',
          asin: heroProduct?.amazonListing?.asin || 'B0HNV8HL1L',
          sku: heroProduct?.sku || 'AUR-FBA-001',
          quantity: 1,
          retailCollected: heroProduct?.pricing?.amazonRetailPrice || 35.55
        })
      });
      const data = await res.json();
      if (data.ok && data.logEntry) {
        setLogs(data.logs || [data.logEntry, ...logs]);
        setSalePreviewHtml(data.logEntry.html);
        setSalePreviewSubject(data.logEntry.subject);
        setPreviewTab('sale');
        showToast(`Sent Test Sale Alert Email to ${config.adminEmail}`);
      }
    } catch (_) {
      showToast('Failed to send test sale alert');
    } finally {
      setSendingSale(false);
    }
  };

  if (!active) return null;

  const activePreviewHtml =
    previewTab === 'custom' && selectedLogEntry
      ? selectedLogEntry.html
      : previewTab === 'sale'
      ? salePreviewHtml
      : dailyPreviewHtml;

  const activePreviewSubject =
    previewTab === 'custom' && selectedLogEntry
      ? selectedLogEntry.subject
      : previewTab === 'sale'
      ? salePreviewSubject
      : dailyPreviewSubject;

  const formatTime12h = (time24: string) => {
    const [hStr, mStr] = (time24 || '21:00').split(':');
    const h = parseInt(hStr || '21', 10);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${mStr || '00'} ${suffix}`;
  };

  return (
    <section
      id="view-reports"
      role="tabpanel"
      aria-labelledby="tab-reports"
      className="workspace-panel active"
      style={{ paddingBottom: 72 }}
    >
      <div style={{ width: '100%', maxWidth: '100%', padding: '0 28px', boxSizing: 'border-box' }}>
        {/* JET-BLACK ARCHITECTURAL SPLIT HERO */}
        <div
          style={{
            background: '#09090B',
            color: '#FFFFFF',
            borderRadius: 16,
            padding: '34px 40px',
            marginBottom: 24,
            border: '1px solid #27272A',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
            gap: 32,
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: '#FAF6EC',
                  color: '#9E782F',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '4px 10px',
                  borderRadius: 999
                }}
              >
                08 · AUTOMATED EMAIL REPORTS & REAL-TIME SALE ALERTS
              </span>
              <span style={{ fontSize: 12, color: '#4ADE80', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                ● Daily {formatTime12h(config.dailySummaryTime)} Summary + Instant Sale Watcher Active
              </span>
            </div>

            <h1
              style={{
                fontSize: 'clamp(26px, 2.8vw, 34px)',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                margin: '0 0 12px 0',
                color: '#FFFFFF'
              }}
            >
              Daily 9:00 PM Executive Summary &{' '}
              <span style={{ color: '#D9B668' }}>Instant New Sale Email Alerts</span>
            </h1>

            <p style={{ color: '#D4D4D8', fontSize: 14, lineHeight: 1.6, margin: '0 0 20px 0', maxWidth: 680 }}>
              Automatically emails <strong style={{ color: '#FFFFFF' }}>{config.adminEmail}</strong> every night at{' '}
              <strong style={{ color: '#D9B668' }}>{formatTime12h(config.dailySummaryTime)}</strong> with today's sales,
              ad clicks & cost, MTD progress, and catalog health — plus sends an{' '}
              <strong style={{ color: '#4ADE80' }}>instant alert email the moment any customer order is placed</strong>.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button
                type="button"
                onClick={handleSendDailySummaryNow}
                disabled={sendingDaily}
                style={{
                  background: '#FFFFFF',
                  color: '#09090B',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 18px',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  minHeight: 44
                }}
              >
                {sendingDaily ? 'Sending Today Summary...' : `📧 Send Today's Summary Now (${config.adminEmail})`}
              </button>

              <button
                type="button"
                onClick={handleSendTestSaleAlert}
                disabled={sendingSale}
                style={{
                  background: '#18181B',
                  color: '#D9B668',
                  border: '1px solid #D9B668',
                  borderRadius: 8,
                  padding: '12px 18px',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  minHeight: 44
                }}
              >
                {sendingSale ? 'Sending Sale Alert...' : '💰 Send Test Sale Alert Email'}
              </button>
            </div>
          </div>

          {/* RIGHT SUMMARY TELEMETRY CARD */}
          <div
            style={{
              background: '#18181B',
              border: '1px solid #27272A',
              borderRadius: 12,
              padding: 24,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #27272A',
                paddingBottom: 12,
                marginBottom: 16
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#D9B668', textTransform: 'uppercase' }}>
                ACTIVE EMAIL AUTOMATION ENGINE
              </span>
              <span style={{ fontSize: 11, color: '#4ADE80', fontWeight: 700 }}>
                ● Daemon Running (60s Cron)
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  Rule 01 · Daily Summary
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF', marginTop: 4 }}>
                  {config.dailySummaryEnabled ? `Every Day @ ${formatTime12h(config.dailySummaryTime)}` : 'Paused'}
                </div>
                <div style={{ fontSize: 11, color: '#D9B668', marginTop: 2 }}>
                  Today's Sales, Ads & Fix Log
                </div>
              </div>

              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  Rule 02 · New Sale Alert
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#4ADE80', marginTop: 4 }}>
                  {config.instantSaleAlertEnabled ? 'Every Sale (Instant)' : 'Paused'}
                </div>
                <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
                  Amazon SP-API + Storefront
                </div>
              </div>

              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  Primary Admin Email
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginTop: 4, wordBreak: 'break-all' }}>
                  {config.adminEmail}
                </div>
                <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
                  {config.ccEmail ? `CC: ${config.ccEmail}` : 'No CC configured'}
                </div>
              </div>

              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>
                  Reports Dispatched
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF', marginTop: 4 }}>
                  {logs.length} Sent
                </div>
                <div style={{ fontSize: 11, color: '#D9B668', marginTop: 2 }}>
                  {config.lastDailySentDate ? `Last 9PM: ${config.lastDailySentDate}` : 'Scheduled for 9:00 PM tonight'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SIDE-BY-SIDE WORKSPACE: LEFT = SETTINGS & DISPATCH LOG, RIGHT = LIVE HTML EMAIL PREVIEW */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(520px, 1.15fr) minmax(480px, 1fr)',
            gap: 24,
            alignItems: 'start'
          }}
        >
          {/* LEFT COLUMN: CONFIGURATION + LOG */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* CARD 1: ADMIN EMAIL & SCHEDULE SETTINGS */}
            <div
              style={{
                background: '#FFFFFF',
                border: '1px solid #E4E4E7',
                borderRadius: 14,
                padding: 24
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    ADMIN RECIPIENT & SCHEDULE CONFIGURATION
                  </span>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: '#09090B', margin: '4px 0 0 0' }}>
                    Daily 9:00 PM Report & Instant Sale Triggers
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleSaveConfig(config)}
                  disabled={savingConfig}
                  style={{
                    background: '#09090B',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: 'pointer',
                    minHeight: 40
                  }}
                >
                  {savingConfig ? 'Saving...' : 'Save Settings'}
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 18 }}>
                <div>
                  <label
                    htmlFor="rep-admin-email"
                    style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                  >
                    Admin Email Address (Primary)
                  </label>
                  <input
                    id="rep-admin-email"
                    type="email"
                    value={config.adminEmail}
                    onChange={(e) => setConfig({ ...config, adminEmail: e.target.value })}
                    placeholder="pprevalon@icloud.com"
                    style={{
                      width: '100%',
                      height: 44,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#09090B',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="rep-cc-email"
                    style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                  >
                    Secondary / Backup Email (Optional CC)
                  </label>
                  <input
                    id="rep-cc-email"
                    type="email"
                    value={config.ccEmail}
                    onChange={(e) => setConfig({ ...config, ccEmail: e.target.value })}
                    placeholder="pprevalon@gmail.com"
                    style={{
                      width: '100%',
                      height: 44,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#09090B',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="rep-daily-time"
                    style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                  >
                    Daily Summary Send Time (Default 9:00 PM)
                  </label>
                  <input
                    id="rep-daily-time"
                    type="time"
                    value={config.dailySummaryTime}
                    onChange={(e) => setConfig({ ...config, dailySummaryTime: e.target.value })}
                    style={{
                      width: '100%',
                      height: 44,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      fontSize: 14,
                      fontWeight: 800,
                      color: '#09090B',
                      fontVariantNumeric: 'tabular-nums',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="rep-timezone"
                    style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                  >
                    Operating Timezone
                  </label>
                  <select
                    id="rep-timezone"
                    value={config.timezone}
                    onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                    style={{
                      width: '100%',
                      height: 44,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      background: '#FFFFFF',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#09090B',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="America/New_York">Eastern Time (ET · New York / Miami)</option>
                    <option value="America/Chicago">Central Time (CT · Chicago / Austin)</option>
                    <option value="America/Denver">Mountain Time (MT · Denver)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT · Los Angeles)</option>
                  </select>
                </div>
              </div>

              {/* AUTOMATION TOGGLE CARDS */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 14,
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: config.dailySummaryEnabled ? '#FAF6EC' : '#F8F8F9',
                    border: config.dailySummaryEnabled ? '2px solid #9E782F' : '1px solid #E4E4E7',
                    cursor: 'pointer'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#09090B' }}>
                      🕘 Send "Today's Executive Summary" Email Every Day at {formatTime12h(config.dailySummaryTime)}
                    </div>
                    <div style={{ fontSize: 12, color: '#3F3F46', marginTop: 3 }}>
                      Includes today's orders, gross sales, net profit, Amazon Sponsored Products clicks & cost, MTD pace vs. monthly goal, and catalog health.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.dailySummaryEnabled}
                    onChange={(e) => {
                      const next = { ...config, dailySummaryEnabled: e.target.checked };
                      setConfig(next);
                      handleSaveConfig(next);
                    }}
                    style={{ width: 20, height: 20, accentColor: '#09090B', marginTop: 2 }}
                  />
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 14,
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: config.instantSaleAlertEnabled ? '#FAF6EC' : '#F8F8F9',
                    border: config.instantSaleAlertEnabled ? '2px solid #9E782F' : '1px solid #E4E4E7',
                    cursor: 'pointer'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#09090B' }}>
                      💰 Send Instant Email Every Time a Sale Is Made (24/7 Live Watcher)
                    </div>
                    <div style={{ fontSize: 12, color: '#3F3F46', marginTop: 3 }}>
                      Emails {config.adminEmail} immediately whenever a new customer order appears in Amazon SP-API (BackdropsForLess) or Storefront Checkout, with item, retail price, supplier cost, and net profit locked.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.instantSaleAlertEnabled}
                    onChange={(e) => {
                      const next = { ...config, instantSaleAlertEnabled: e.target.checked };
                      setConfig(next);
                      handleSaveConfig(next);
                    }}
                    style={{ width: 20, height: 20, accentColor: '#09090B', marginTop: 2 }}
                  />
                </label>
              </div>

              {/* COLLAPSIBLE SMTP / APP PASSWORD TRANSPORT CONFIG */}
              <div style={{ borderTop: '1px solid #E4E4E7', paddingTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setShowSmtpSettings(!showSmtpSettings)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 12,
                    fontWeight: 800,
                    color: '#9E782F',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <span>{showSmtpSettings ? '▼' : '▶'}</span>
                  <span>Configure Direct SMTP / iCloud App Password or Resend API Key (Optional)</span>
                </button>

                {showSmtpSettings && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 16,
                      background: '#F8F8F9',
                      borderRadius: 10,
                      border: '1px solid #E4E4E7',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 12
                    }}
                  >
                    <div>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                        SMTP Server Host
                      </label>
                      <input
                        type="text"
                        value={config.smtpHost}
                        onChange={(e) => setConfig({ ...config, smtpHost: e.target.value })}
                        placeholder="smtp.mail.me.com"
                        style={{ width: '100%', height: 38, padding: '0 10px', borderRadius: 6, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                        SMTP Port (587 or 465)
                      </label>
                      <input
                        type="number"
                        value={config.smtpPort}
                        onChange={(e) => setConfig({ ...config, smtpPort: Number(e.target.value) || 587 })}
                        style={{ width: '100%', height: 38, padding: '0 10px', borderRadius: 6, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                        iCloud / Gmail App Password
                      </label>
                      <input
                        type="password"
                        value={config.smtpPass}
                        onChange={(e) => setConfig({ ...config, smtpPass: e.target.value })}
                        placeholder="xxxx-xxxx-xxxx-xxxx"
                        style={{ width: '100%', height: 38, padding: '0 10px', borderRadius: 6, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}>
                        Or Resend API Key (re_...)
                      </label>
                      <input
                        type="password"
                        value={config.resendApiKey}
                        onChange={(e) => setConfig({ ...config, resendApiKey: e.target.value })}
                        placeholder="re_123456789..."
                        style={{ width: '100%', height: 38, padding: '0 10px', borderRadius: 6, border: '1px solid #D4D4D8', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* CARD 2: SENT REPORTS & SALE ALERTS DISPATCH LOG */}
            <div
              style={{
                background: '#FFFFFF',
                border: '1px solid #E4E4E7',
                borderRadius: 14,
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  padding: '18px 24px',
                  borderBottom: '1px solid #E4E4E7',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    DISPATCH HISTORY & AUDIT LOG
                  </span>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: '#09090B', margin: '2px 0 0 0' }}>
                    Sent 9:00 PM Summaries & Instant Sale Alerts ({logs.length})
                  </h3>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                  <thead>
                    <tr style={{ background: '#09090B', color: '#FFFFFF', textAlign: 'left' }}>
                      <th scope="col" style={{ padding: '10px 14px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Sent Time</th>
                      <th scope="col" style={{ padding: '10px 14px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Type</th>
                      <th scope="col" style={{ padding: '10px 14px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Recipient</th>
                      <th scope="col" style={{ padding: '10px 14px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Subject</th>
                      <th scope="col" style={{ padding: '10px 14px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#71717A' }}>
                          No emails dispatched yet today. Next automated summary is scheduled for{' '}
                          <strong>{formatTime12h(config.dailySummaryTime)}</strong>, or click{' '}
                          <strong>"Send Today's Summary Now"</strong> above to test immediately.
                        </td>
                      </tr>
                    ) : (
                      logs.map((item) => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #E4E4E7' }}>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#3F3F46', fontWeight: 600 }}>
                            {new Date(item.sentAt).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                padding: '3px 8px',
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 800,
                                background: item.type === 'instant_sale_alert' ? '#09090B' : '#FAF6EC',
                                color: item.type === 'instant_sale_alert' ? '#4ADE80' : '#9E782F',
                                border: item.type === 'instant_sale_alert' ? 'none' : '1px solid #D9B668'
                              }}
                            >
                              {item.type === 'instant_sale_alert' ? '💰 Sale Alert' : '🕘 9PM Summary'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: '#09090B' }}>{item.to}</td>
                          <td style={{ padding: '10px 14px', color: '#3F3F46', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.subject}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedLogEntry(item);
                                setPreviewTab('custom');
                              }}
                              style={{
                                background: '#F8F8F9',
                                border: '1px solid #D4D4D8',
                                borderRadius: 6,
                                padding: '5px 10px',
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#09090B',
                                cursor: 'pointer'
                              }}
                            >
                              View Email
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: LIVE INTERACTIVE EMAIL PREVIEW PANE */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 14,
              padding: 24,
              position: 'sticky',
              top: 84
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  LIVE HTML EMAIL TEMPLATE PREVIEW
                </span>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#09090B', margin: '4px 0 0 0' }}>
                  {previewTab === 'sale'
                    ? 'Instant New Sale Alert Email'
                    : previewTab === 'custom' && selectedLogEntry
                    ? `Dispatched Email (${new Date(selectedLogEntry.sentAt).toLocaleTimeString()})`
                    : `Today's ${formatTime12h(config.dailySummaryTime)} Executive Summary Email`}
                </h3>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setPreviewTab('daily')}
                  style={{
                    padding: '7px 12px',
                    borderRadius: 8,
                    border: previewTab === 'daily' ? '2px solid #09090B' : '1px solid #D4D4D8',
                    background: previewTab === 'daily' ? '#09090B' : '#FFFFFF',
                    color: previewTab === 'daily' ? '#FFFFFF' : '#09090B',
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  🕘 9:00 PM Summary
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTab('sale')}
                  style={{
                    padding: '7px 12px',
                    borderRadius: 8,
                    border: previewTab === 'sale' ? '2px solid #09090B' : '1px solid #D4D4D8',
                    background: previewTab === 'sale' ? '#09090B' : '#FFFFFF',
                    color: previewTab === 'sale' ? '#FFFFFF' : '#09090B',
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  💰 Instant Sale Alert
                </button>
              </div>
            </div>

            {/* EMAIL ENVELOPE HEADER */}
            <div
              style={{
                background: '#F8F8F9',
                border: '1px solid #E4E4E7',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 14,
                fontSize: 12
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <strong style={{ color: '#71717A', textTransform: 'uppercase', fontSize: 10 }}>To:</strong>{' '}
                <strong style={{ color: '#09090B' }}>{config.adminEmail}</strong>
                {config.ccEmail ? <span style={{ color: '#71717A' }}> (CC: {config.ccEmail})</span> : null}
              </div>
              <div>
                <strong style={{ color: '#71717A', textTransform: 'uppercase', fontSize: 10 }}>Subject:</strong>{' '}
                <strong style={{ color: '#09090B' }}>{activePreviewSubject}</strong>
              </div>
            </div>

            {/* RENDERED HTML EMAIL BODY */}
            <div
              style={{
                border: '1px solid #E4E4E7',
                borderRadius: 10,
                overflow: 'hidden',
                background: '#F4F4F5'
              }}
            >
              <iframe
                title="Live Email Report Preview"
                srcDoc={activePreviewHtml}
                style={{
                  width: '100%',
                  height: 620,
                  border: 'none',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
